import React, { ElementType, PropsWithChildren } from "react";
import {
  Box,
  Button,
  Collapse,
  Flex,
  Stack,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaHistory } from "react-icons/fa";
import {
  FiAlertOctagon,
  FiAlertTriangle,
  FiBookOpen,
  FiBookmark,
  FiChevronDown,
  FiChevronRight,
  FiFastForward,
  FiInfo,
} from "react-icons/fi";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { VscJson } from "@react-icons/all-files/vsc/VscJson";
import { VscNote } from "@react-icons/all-files/vsc/VscNote";
import { useButtonColor } from "../utils";
import { useToggle } from "react-use";

type SectionConfig = {
  icon: ElementType;
  title: (title?: string) => string;
  colorScheme?: string;
};

const SECTION_CONFIGS = {
  code: {
    icon: VscJson,
    title: (title) => title,
  } as SectionConfig,
  image: {
    icon: VscNote,
    title: () => "Expand for image description",
  } as SectionConfig,
  optional: {
    icon: FiBookmark,
    title: (title) => title,
  } as SectionConfig,
  learn: {
    icon: FiBookOpen,
    title: (title) => (title ? `Learn more: ${title}` : "Learn more"),
    colorScheme: "blue",
  } as SectionConfig,
  info: {
    icon: FiInfo,
    title: (title) => (title ? `Learn more: ${title}` : "Learn more"),
    colorScheme: "blue",
  } as SectionConfig,
  refresher: {
    icon: FaHistory,
    title: (title) => (title ? `Refresher: ${title}` : "Refresher"),
    colorScheme: "blue",
  } as SectionConfig,
  error: {
    icon: FiAlertOctagon,
    title: (title) =>
      title ? `Something wrong with ${title}?` : "Something wrong?",
    colorScheme: "red",
  } as SectionConfig,
  warning: {
    icon: FiAlertTriangle,
    title: (title) => `Watch out! ${title}`,
    colorScheme: "yellow",
  } as SectionConfig,
  next: {
    icon: FiFastForward,
    title: (title) => (title ? `Coming soon: ${title}` : "Coming soon"),
    colorScheme: "green",
  } as SectionConfig,
};

type CollapsibleSectionProps = PropsWithChildren<{
  type?: keyof typeof SECTION_CONFIGS;
  title?: string;
  icon?: ElementType;
  colorScheme?: string;
}>;

export function CollapsibleSection({
  type,
  children,
  ...props
}: CollapsibleSectionProps) {
  const [isOpen, toggleOpen] = useToggle(false);
  const {
    title = "See more",
    icon,
    colorScheme = "blue",
  } = type ? SECTION_CONFIGS[type] : props;
  const borderColor = useButtonColor(colorScheme);
  return (
    <>
      <Button
        fontSize="lg"
        onClick={toggleOpen}
        isFullWidth
        justifyContent="space-between"
        borderBottomRadius={isOpen ? "none" : "md"}
        colorScheme={colorScheme}
        color="silver.200"
        _dark={{ color: "black.100" }}
        variant="solid"
      >
        <Flex
          align="center"
          sx={{
            code: {
              mx: 1,
            },
          }}
        >
          {icon && <Box mr="2" as={icon} />}
          <MarkdownRenderer>
            {typeof title === "function" ? title(props.title) : title}
          </MarkdownRenderer>
        </Flex>
        {isOpen ? <FiChevronDown /> : <FiChevronRight />}
      </Button>
      <Collapse in={isOpen} animateOpacity>
        <Stack
          p="4"
          spacing="4"
          borderWidth="2px"
          borderColor={borderColor}
          borderBottomRadius="md"
          borderTop="none"
        >
          {children}
        </Stack>
      </Collapse>
    </>
  );
}
